<template>
  <div class="login-container">
    <div class="left-section">
      <video
        class="hero-video"
        :key="heroSrc"
        :src="heroSrc"
        :poster="heroPosterSrc"
        autoplay
        muted
        loop
        playsinline
      ></video>
      <div class="logo">
        <img :src="logoWhite" alt="Peelo Logo" />
      </div>
      <a href="https://peelo.io" class="back-link">
        <span>{{ $t('common.backToSite') }}</span>
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M5 12h14M12 5l7 7-7 7"/>
        </svg>
      </a>
      <div class="hero-content">
        <TransitionGroup name="fade-slide">
          <div v-for="(slide, index) in slides" :key="'slide-' + index" v-show="currentSlide === index" class="slide">
            <h2>{{ slide.title }}</h2>
            <p>{{ slide.description }}</p>
          </div>
        </TransitionGroup>
        <div class="dots">
          <span 
            v-for="(slide, index) in slides" 
            :key="'dot-' + index"
            :class="['dot', { active: currentSlideIndex === index }]"
            @click="setSlide(index)"
          ></span>
        </div>
      </div>
    </div>
    <div class="right-section">
      <div class="auth-container">
        <h1>{{ $t('auth.login') }}</h1>
        

        <!-- Google Login -->
        <div class="google-auth-section">
          <GoogleLogin :callback="handleGoogleCallback">
            <button type="button" class="login-button google-login-btn">
              <svg class="google-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 48 48">
                <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"></path>
                <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"></path>
                <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.222,0-9.653-3.343-11.303-8l-6.571,4.819C9.656,39.663,16.318,44,24,44z"></path>
                <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"></path>
              </svg>
              <span>{{ $t('auth.googleLogin') }}</span>
            </button>
          </GoogleLogin>
        </div>
        
        <div class="separator" style="text-align: center; margin-bottom: 20px;">
           <span>{{ $t('auth.orLoginWithEmail') || 'ou se connecter avec email' }}</span>
        </div>

        <form @submit.prevent="loginUser" class="login-form">
          <div class="form-group">
            <label for="email">{{ $t('auth.email') }}</label>
            <input 
              type="email" 
              id="email" 
              v-model="loginData.email" 
              :placeholder="$t('auth.emailPlaceholder')"
              required
              :class="{ 'input-error': errorMessage }"
            />
          </div>
          
          <div class="form-group">
            <label for="password">{{ $t('auth.password') }}</label>
            <div class="password-input-wrapper">
              <input 
                :type="showPassword ? 'text' : 'password'" 
                id="password" 
                v-model="loginData.password" 
                :placeholder="$t('auth.passwordPlaceholder')"
                required
                :class="{ 'input-error': errorMessage }"
              />
              <span class="password-toggle" @click="togglePassword">
                <svg v-if="!showPassword" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                <svg v-else xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
              </span>
            </div>
          </div>
          
          <div v-if="errorMessage" class="error-message">
            {{ errorMessage }}
          </div>
          
          <div class="forgot-password-container">
            <router-link to="/forgot-password" class="forgot-password-link">{{ $t('auth.forgotPassword') }}</router-link>
          </div>
          
          <button type="submit" class="login-button" :disabled="isLoading">
            <span v-if="isLoading" class="loader"></span>
            <span v-else>{{ $t('auth.loginButton') }}</span>
          </button>
        </form>

        <div class="auth-footer">
          <div class="separator">
            <span>{{ $t('common.or') }}</span>
          </div>
          
          <div class="register-prompt">
            <span>{{ $t('auth.noAccount') }}</span>
            <router-link to="/register" class="register-button">
              {{ $t('auth.createAccount') }}
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </router-link>
          </div>
        </div>

        <!-- Quick Access Links -->
        <div class="quick-access-section">
          <p class="quick-access-title">{{ $t('login.quickAccess') }}</p>
          <div class="quick-access-links">
            <a href="https://admin.peelo.shop" target="_blank" class="quick-access-link ecommerce">
              <div class="quick-access-icon">
                <!-- Shopping Cart Icon -->
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="9" cy="21" r="1"/>
                  <circle cx="20" cy="21" r="1"/>
                  <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
                </svg>
              </div>
              <div class="quick-access-content">
                <span class="quick-access-name">{{ $t('login.ecommerceAdmin') }}</span>
                <span class="quick-access-desc">{{ $t('login.ecommerceDesc') }}</span>
              </div>
              <svg class="quick-access-arrow" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                <polyline points="15 3 21 3 21 9"/>
                <line x1="10" y1="14" x2="21" y2="3"/>
              </svg>
            </a>
            <a href="https://console.peelo.io/" target="_blank" class="quick-access-link console">
              <div class="quick-access-icon">
                <!-- Terminal/Code Icon -->
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="4 17 10 11 4 5"/>
                  <line x1="12" y1="19" x2="20" y2="19"/>
                </svg>
              </div>
              <div class="quick-access-content">
                <span class="quick-access-name">{{ $t('login.consoleApi') }}</span>
                <span class="quick-access-desc">{{ $t('login.consoleDesc') }}</span>
              </div>
              <svg class="quick-access-arrow" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                <polyline points="15 3 21 3 21 9"/>
                <line x1="10" y1="14" x2="21" y2="3"/>
              </svg>
            </a>
          </div>
        </div>
        
        
      </div>
    </div>
  </div>
</template>

<script>
import { useRouter } from "vue-router";
import { ref, onMounted, onBeforeUnmount, computed } from 'vue';
import logoWhite from '@/assets/logo-white.png';
import heroVideo from '@/assets/hero-3d.mp4';
import heroVideoMobile from '@/assets/hero-3d-mobile.mp4';
import heroPoster from '@/assets/hero-3d-poster.webp';
import heroPosterMobile from '@/assets/hero-3d-poster-mobile.webp';
import { useI18n } from 'vue-i18n';
import { apiBaseUrl } from '@/config';
import axios from 'axios';
import { setSsoCookie, getSsoCookie, decodeJwtPayload } from '@/utils/ssoCookie';
import { redirectAfterLogin } from '@/utils/postLogin';

export default {
  name: 'UserLogin',
  components: {
  },
  setup() {
    const { t } = useI18n();
    const router = useRouter();
    const currentSlideIndex = ref(0);
    const currentSlide = ref(0);
    const isTransitioning = ref(false);
    const user = ref(null);
    const isLoading = ref(false);
    const errorMessage = ref('');
    const showPassword = ref(false);
    
    const loginData = ref({
      email: '',
      password: ''
    });

    // Vidéo hero : 9:16 sur desktop, 16:9 sur mobile (même breakpoint que le CSS)
    const heroMediaQuery = window.matchMedia('(max-width: 768px)');
    const isMobileHero = ref(heroMediaQuery.matches);
    const onHeroMediaChange = (e) => { isMobileHero.value = e.matches; };
    const heroSrc = computed(() => (isMobileHero.value ? heroVideoMobile : heroVideo));
    const heroPosterSrc = computed(() => (isMobileHero.value ? heroPosterMobile : heroPoster));
    
    const slides = computed(() => [
      {
        title: t('slides.slide1.title'),
        description: t('slides.slide1.description')
      },
      {
        title: t('slides.slide2.title'),
        description: t('slides.slide2.description')
      },
      {
        title: t('slides.slide3.title'),
        description: t('slides.slide3.description')
      }
    ]);

    let slideInterval;

    const setSlide = (index) => {
      if (isTransitioning.value) return;
      isTransitioning.value = true;
      currentSlideIndex.value = index;
      currentSlide.value = index;
      setTimeout(() => {
        isTransitioning.value = false;
      }, 800);

    };

    const handleGoogleCallback = async (response) => {
      console.log("🔍 Google Response Raw:", response);
      try {
        isLoading.value = true;
        errorMessage.value = '';
        
        // Si on reçoit un code d'autorisation, on doit l'échanger contre un access_token
        if (response.code) {
          console.log("📋 Code d'autorisation reçu, échange en cours...");
          
          try {
            // Échanger le code contre un access_token
            const tokenResponse = await axios.post('https://oauth2.googleapis.com/token', {
              code: response.code,
              client_id: '14194066058-h4ompr7u89dkvbuh6fpfdlimhlea5p9p.apps.googleusercontent.com',
              redirect_uri: window.location.origin,
              grant_type: 'authorization_code'
            });
            
            console.log("✅ Access token obtenu");
            
            // Utiliser l'access_token pour récupérer les infos utilisateur
            const userInfoResponse = await axios.get('https://www.googleapis.com/oauth2/v3/userinfo', {
              headers: {
                Authorization: `Bearer ${tokenResponse.data.access_token}`
              }
            });
            
            const userInfo = userInfoResponse.data;
            console.log("✅ Informations utilisateur récupérées:", userInfo.email);
            
            // Envoyer les infos utilisateur au backend pour login/création
            const res = await axios.post(`${apiBaseUrl}/secure/auth/google/userinfo`, {
              email: userInfo.email,
              name: userInfo.name,
              picture: userInfo.picture,
              googleId: userInfo.sub
            });
            
            if (res.data && res.data.token) {
              const userData = res.data.data;
              const sessionToken = res.data.token;
              
              // Ajouter le token dans userData pour correspondre au format email/password
              userData.token = sessionToken;
              localStorage.setItem("user", JSON.stringify({ data: userData }));
              setSsoCookie(sessionToken);

              if (userData.userType === 'peeloshop') {
                window.location.href = "https://admin.peelo.shop";
              } else {
                await redirectAfterLogin(router, sessionToken);
              }
            }
            
          } catch (exchangeError) {
            console.error("❌ Erreur lors de l'échange du code:", exchangeError);
            errorMessage.value = "Erreur lors de la connexion Google. Veuillez réessayer.";
          }
          
        } else if (response.credential) {
          // Fallback: si on reçoit directement un credential (ID token)
          console.log("📋 ID Token reçu directement");
          const res = await axios.post(`${apiBaseUrl}/secure/auth/google`, { token: response.credential });
          
          if (res.data && res.data.token) {
            const userData = res.data.data;
            const sessionToken = res.data.token;
            
            localStorage.setItem("user", JSON.stringify({ data: userData, token: sessionToken }));
            setSsoCookie(sessionToken);

            if (userData.userType === 'peeloshop') {
              window.location.href = "https://admin.peelo.shop";
            } else {
              await redirectAfterLogin(router, sessionToken);
            }
          }
        } else if (response.access_token) {
          // Cas access_token (popup flow) - comme agenttalk !
          console.log("📋 Access Token reçu directement (popup flow)");
          
          try {
            // Utiliser l'access_token pour récupérer les infos utilisateur
            const userInfoResponse = await axios.get('https://www.googleapis.com/oauth2/v3/userinfo', {
              headers: {
                Authorization: `Bearer ${response.access_token}`
              }
            });
            
            const userInfo = userInfoResponse.data;
            console.log("✅ Informations utilisateur récupérées:", userInfo.email);
            
            // Envoyer les infos utilisateur au backend pour login/création
            const res = await axios.post(`${apiBaseUrl}/secure/auth/google/userinfo`, {
              email: userInfo.email,
              name: userInfo.name,
              picture: userInfo.picture,
              googleId: userInfo.sub
            });
            
            if (res.data && res.data.token) {
              const userData = res.data.data;
              const sessionToken = res.data.token;
              
              // Ajouter le token dans userData pour correspondre au format email/password
              userData.token = sessionToken;
              localStorage.setItem("user", JSON.stringify({ data: userData }));
              setSsoCookie(sessionToken);

              // Vérifier si l'inscription est complète
              if (userData.registration_incomplete) {
                // Rediriger vers onboarding pour compléter l'inscription
                await router.push("/onboarding");
              } else if (userData.userType === 'peeloshop') {
                window.location.href = "https://admin.peelo.shop";
              } else {
                await redirectAfterLogin(router, sessionToken);
              }
            }
          } catch (accessTokenError) {
            console.error("❌ Erreur lors de l'utilisation de l'access token:", accessTokenError);
            errorMessage.value = "Erreur lors de la connexion Google. Veuillez réessayer.";
          }
        } else {
          console.error("❌ Ni code ni credential dans la réponse:", response);
          errorMessage.value = "Erreur: Format de réponse Google invalide.";
        }
        
      } catch (error) {
        console.error("Google Auth Error", error);
        errorMessage.value = "Erreur d'authentification Google. Veuillez réessayer.";
      } finally {
        isLoading.value = false;
      }
    };

    const startSlideShow = () => {
      slideInterval = setInterval(() => {
        setSlide((currentSlideIndex.value + 1) % slides.value.length);
      }, 7000);
    };
    
    const loginUser = async () => {
      try {
        // Réinitialiser le message d'erreur
        errorMessage.value = '';
        isLoading.value = true;
        
        // Vérifier les champs obligatoires
        if (!loginData.value.email || !loginData.value.password) {
          errorMessage.value = 'Veuillez remplir tous les champs';
          isLoading.value = false;
          return;
        }
        
        // Préparer les données à envoyer
        const requestData = {
          email: loginData.value.email,
          password: loginData.value.password
        };
        
        
        
        // Envoyer la requête d'authentification
        const response = await axios.post(`${apiBaseUrl}/verify/user`, requestData);
        
        
        
        if (response.data && response.data.data) {
          // Stocker les informations de l'utilisateur
          const userData = {
            id: response.data.data.id,
            name: response.data.data.name,
            email: response.data.data.email,
            tel: response.data.data.tel,
            profile_picture: response.data.data.profile_picture,
            provenance: response.data.data.provenance || 'email',
            subscription: response.data.data.subscription || null,
            pointing_chatbot_stats: response.data.data.pointing_chatbot_stats,
            token: response.data.token
          };
          
          localStorage.setItem('user', JSON.stringify({ data: userData }));
          setSsoCookie(userData.token);

          // Sur mobile -> playground de l'agent ; sur desktop -> hub /dashboard.
          await redirectAfterLogin(router, userData.token);
        } else {
          throw new Error('Données utilisateur invalides dans la réponse');
        }
      } catch (error) {
        //console.error('Erreur de connexion:', error);
        
        // Gérer les différents types d'erreurs
        if (error.response) {
          // Le serveur a répondu avec un statut d'erreur
          switch (error.response.status) {
            case 400:  
              errorMessage.value = 'Email ou mot de passe manquant';
              break;
            case 401:
              errorMessage.value = 'Mot de passe incorrect';
              break;
            case 404:
              errorMessage.value = 'Utilisateur introuvable';
              break;
            default:
              errorMessage.value = error.response.data?.error || 'Erreur lors de la connexion';
          }
        } else if (error.request) {
          // La requête a été faite mais aucune réponse n'a été reçue
          errorMessage.value = 'Impossible de joindre le serveur. Veuillez réessayer plus tard.';
        } else {
          // Une erreur s'est produite lors de la configuration de la requête
          errorMessage.value = 'Une erreur est survenue. Veuillez réessayer.';
        }
      } finally {
        isLoading.value = false;
      }
    };

    const togglePassword = () => {
      showPassword.value = !showPassword.value;
    };

    onMounted(() => {
      heroMediaQuery.addEventListener('change', onHeroMediaChange);
      startSlideShow();
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          user.value = parsedUser.data;
          if (user.value) {
            redirectAfterLogin(router, user.value.token);
          }
        } catch (error) {
          //console.error('Error parsing stored user data:', error);
          localStorage.removeItem('user');
        }
      } else {
        // SSO *.peelo.io : pas de session locale, mais un cookie posé par un autre
        // sous-domaine (ex: console.peelo.io) -> on adopte la session sans re-login.
        // Le payload du JWT contient le profil ; la validité réelle est re-vérifiée
        // par l'API à chaque requête (401 -> retour login).
        const ssoToken = getSsoCookie();
        if (ssoToken) {
          const payload = decodeJwtPayload(ssoToken);
          if (payload && payload.email && (!payload.exp || payload.exp * 1000 > Date.now())) {
            const userData = {
              id: payload._id,
              name: payload.name,
              email: payload.email,
              tel: payload.tel,
              profile_picture: payload.profile_picture,
              provenance: payload.provenance || 'email',
              subscription: payload.subscription || null,
              pointing_chatbot_stats: payload.pointing_chatbot_stats,
              token: ssoToken
            };
            localStorage.setItem('user', JSON.stringify({ data: userData }));
            redirectAfterLogin(router, ssoToken);
          }
        }
      }
    });

    onBeforeUnmount(() => {
      heroMediaQuery.removeEventListener('change', onHeroMediaChange);
      clearInterval(slideInterval);
    });

    return {
      router,
      currentSlideIndex,
      currentSlide,
      slides,
      setSlide,
      apiBaseUrl,
      user,
      loginData,
      loginUser,
      isLoading,
      errorMessage,
      showPassword,
      togglePassword,
      logoWhite,
      heroSrc,
      heroPosterSrc,
      handleGoogleCallback
    };
  }
};
</script>

<style scoped>
.login-container {
  min-height: 100vh;
  display: grid;
  grid-template-columns: 1fr 1fr;
  background: linear-gradient(160deg, var(--dark-bg) 0%, var(--dark-bg-deep) 100%);
  overflow: hidden;
}

.left-section {
  position: relative;
  overflow: hidden;
  border-radius: 24px;
  margin: 1rem;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 2rem);
  max-height: calc(100vh - 2rem);
}

/* Sélecteur doublé pour battre la règle `.left-section > *` (position/z-index) */
.left-section .hero-video {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.8s ease;
  z-index: 0;
}

.left-section:hover .hero-video {
  transform: scale(1.08);
}

.left-section::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to bottom, rgba(37, 211, 102, 0.6), rgba(37, 211, 102, 0.2));
  z-index: 1;
}

.left-section > * {
  position: relative;
  z-index: 2;
}

.logo {
  height: 48px;
}

.logo img {
  height: 100%;
  object-fit: contain;
}

.back-link {
  position: absolute;
  top: 2rem;
  right: 2rem;
  color: white;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: rgba(18, 140, 126, 0.2);
  border-radius: 20px;
  font-size: 14px;
  transition: background-color 0.2s;
}

.back-link:hover {
  background: rgba(18, 140, 126, 0.3);
}

.hero-content {
  margin-top: auto;
  margin-bottom: 3rem;
  color: white;
  position: relative;
  height: auto;
  min-height: 220px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  overflow: visible;
}

.slide {
  width: 100%;
  overflow: visible;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}

.slide h2 {
  font-size: 2.5rem;
  font-weight: 600;
  margin-bottom: 1rem;
}

.slide p {
  font-size: 1.2rem;
  line-height: 1.5;
  opacity: 0.9;
  margin-bottom: 2rem;
}

/* Enhanced transitions */
.fade-slide-enter-active {
  transition: all 0.8s ease-out;
  transition-delay: 0.2s;
}

.fade-slide-leave-active {
  transition: all 0.6s ease-in;
  position: absolute;
}

.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(30px);
}

.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-30px);
}

.fade-slide-move {
  transition: transform 0.8s ease;
}

.dots {
  display: flex;
  gap: 0.8rem;
  margin-top: 140px;
  justify-content: center;
}

.dot {
  width: 2.5rem;
  height: 4px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 2px;
  cursor: pointer;
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

.dot:hover {
  background: rgba(255, 255, 255, 0.4);
}

.dot.active {
  background: white;
  width: 4rem;
}

.right-section {
  padding: 2rem 4rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow-y: auto;
  overflow-x: hidden;
}

.auth-container {
  max-width: 400px;
  width: 100%;
  overflow: hidden;
}

h1 {
  color: white;
  font-size: 2.5rem;
  font-weight: 500;
  margin-bottom: 2.5rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.login-text {
  color: #8f8f8f;
  margin-bottom: 2rem;
}

.login-link {
  color: white;
  text-decoration: none;
  font-weight: 500;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  color: white;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
}

.form-group input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  background: transparent;
  color: white;
  font-size: 1rem;
}

.form-group input:focus {
  outline: none;
  border-color: rgba(255, 255, 255, 0.3);
}

.input-error {
  border-color: #ff5252 !important;
}

.error-message {
  color: #ff5252;
  font-size: 0.875rem;
  margin-bottom: 1rem;
  padding: 0.75rem;
  background-color: rgba(255, 82, 82, 0.1);
  border-radius: 8px;
  border-left: 3px solid #ff5252;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.login-button {
  width: 100%;
  padding: 0.75rem;
  border: none;
  border-radius: 8px;
  background: white;
  color: var(--dark-bg);
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.login-button:hover {
  background: rgba(255, 255, 255, 0.9);
  transform: translateY(-1px);
}

.login-button:active {
  transform: translateY(0);
}

.login-button:disabled {
  background: rgba(255, 255, 255, 0.5);
  cursor: not-allowed;
}

.google-login-btn {
  background: white !important;
  color: var(--dark-bg) !important;
  gap: 12px;
  border: 1px solid rgba(0,0,0,0.1) !important;
  margin-bottom: 0; /* Let container handle spacing if needed, but previously section had it */
}
.google-login-btn:hover {
  background: #f8f9fa !important;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}
.google-icon {
  width: 20px;
  height: 20px;
}
.google-auth-section {
    width: 100%;
}
/* Ensure the GoogleLogin wrapper doesn't shrink */
.google-auth-section > div { 
    width: 100%; 
}

.loader {
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid rgba(20, 48, 40, 0.3);
  border-radius: 50%;
  border-top-color: var(--dark-bg);
  animation: spin 1s ease-in-out infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.terms {
  margin-top: 2rem;
  font-size: 0.875rem;
  color: #8f8f8f;
  text-align: center;
}

.terms a {
  color: white;
  text-decoration: none;
  font-weight: 500;
}

.terms a:hover {
  text-decoration: underline;
}

@media (max-width: 768px) {
  .login-container {
    grid-template-columns: 1fr;
    overflow-y: auto;
  }

  /* Le formulaire d'abord : l'utilisateur voit les champs sans scroller */
  .right-section {
    order: 1;
    padding: 1.5rem 1.25rem 2rem;
    min-height: auto;
  }

  /* L'image devient une bannière compacte sous le formulaire */
  .left-section {
    order: 2;
    min-height: 240px;
    max-height: 280px;
    margin: 0.75rem;
    padding: 1.25rem;
  }

  .logo {
    height: 32px;
  }

  .back-link {
    top: 1rem;
    right: 1rem;
    padding: 0.35rem 0.75rem;
    font-size: 13px;
  }

  .hero-content {
    margin-bottom: 0.5rem;
    min-height: 120px;
  }

  .auth-container {
    margin: 0 auto;
    padding: 0 1rem;
  }

  h1 {
    font-size: 2rem;
  }

  .slide h2 {
    font-size: 1.4rem;
  }

  .slide p {
    font-size: 0.9rem;
  }

  .quick-access-section {
    display: none;
  }
}

.auth-footer {
  margin-top: 2rem;
}

.separator {
  display: flex;
  align-items: center;
  text-align: center;
  margin: 2rem 0;
  color: #8f8f8f;
}

.separator::before,
.separator::after {
  content: '';
  flex: 1;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.separator span {
  padding: 0 10px;
}

.register-prompt {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin: 1.5rem 0;
}

.register-prompt span {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.95rem;
}

.register-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: var(--dark-accent-muted);
  border-radius: 8px;
  color: var(--dark-accent);
  text-decoration: none;
  font-weight: 500;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.register-button:hover {
  background: rgba(218, 255, 209, 0.25);
  transform: translateX(5px);
}

.register-button svg {
  transition: transform 0.2s ease;
}

.register-button:hover svg {
  transform: translateX(3px);
}

.login-form {
  margin-top: 1rem;
}

.forgot-password-container {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1.5rem;
}

.forgot-password-link {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.6);
  text-decoration: none;
  transition: color 0.2s ease;
}

.forgot-password-link:hover {
  color: var(--dark-accent);
  text-decoration: underline;
}

.password-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.password-input-wrapper input {
  padding-right: 2.5rem;
}

.password-toggle {
  position: absolute;
  right: 12px;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
  z-index: 10;
}

.password-toggle:hover {
  color: white;
}

/* Quick Access Section */
.quick-access-section {
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.quick-access-title {
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 1rem;
  text-align: center;
}

.quick-access-links {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.quick-access-link {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border-radius: 12px;
  text-decoration: none;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.02);
}

.quick-access-link:hover {
  transform: translateY(-2px);
  border-color: rgba(255, 255, 255, 0.2);
}

.quick-access-link.ecommerce:hover {
  background: rgba(255, 159, 67, 0.1);
  border-color: rgba(255, 159, 67, 0.3);
}

.quick-access-link.console:hover {
  background: rgba(99, 102, 241, 0.1);
  border-color: rgba(99, 102, 241, 0.3);
}

.quick-access-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.quick-access-link.ecommerce .quick-access-icon {
  background: linear-gradient(135deg, rgba(255, 159, 67, 0.2), rgba(255, 107, 53, 0.2));
  color: #ff9f43;
}

.quick-access-link.console .quick-access-icon {
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.2), rgba(139, 92, 246, 0.2));
  color: #818cf8;
}

.quick-access-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.quick-access-name {
  color: white;
  font-weight: 600;
  font-size: 0.95rem;
}

.quick-access-desc {
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.8rem;
}

.quick-access-arrow {
  color: rgba(255, 255, 255, 0.3);
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.quick-access-link:hover .quick-access-arrow {
  color: rgba(255, 255, 255, 0.6);
  transform: translate(3px, -3px);
}
</style>
